import moment from "moment";

// ── Dati dimostrativi per i moduli non ancora attivi ────────────────────────
// Deterministici (stesso seme → stessi dati a ogni caricamento), agganciati ai
// dati reali del club dove possibile e sempre marcati `preview` nella UI.

const hash = (str) => {
	let h = 2166136261;
	for (const c of String(str)) {
		h ^= c.charCodeAt(0);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
};

const seededRandom = (seed) => {
	let x = hash(seed) || 1;
	return () => {
		x ^= x << 13;
		x ^= x >>> 17;
		x ^= x << 5;
		return ((x >>> 0) % 10000) / 10000;
	};
};

const fullName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim();

const findStaff = (s, pattern) =>
	s.staff.find((m) =>
		pattern.test((typeof m.role === "string" ? m.role : m.role?.name) || ""),
	);

// Quote di iscrizione: 3 rate (2, 5 e 8 mesi dopo l'inizio stagione)
export const demoFees = (roster, players, seasonStart) => {
	const start = moment(seasonStart);
	const dues = [2, 5, 8].map((m) => start.clone().add(m, "months"));
	return players.map((p) => {
		const rnd = seededRandom(roster.id + p.id);
		const total = 900 + Math.round(rnd() * 6) * 50;
		const rate = Math.round(total / 3);
		const payer = rnd();
		return {
			playerId: p.id,
			totalAmount: total,
			installments: dues.map((due, i) => ({
				amount: i === 2 ? total - rate * 2 : rate,
				dueDate: due.format("YYYY-MM-DD"),
				status:
					(due.isBefore(moment()) && payer > 0.18) || payer > 0.92
						? "paid"
						: "unpaid",
			})),
		};
	});
};

// Attività aperte dei reparti non ancora gestiti dal sistema
export const demoActivities = (s) => {
	const now = moment();
	const items = [];
	const allPlayers = s.teams.flatMap((t) => t.players);
	const rnd = seededRandom(s.season?.id || "demo");
	const pick = () =>
		allPlayers.length
			? allPlayers[Math.floor(rnd() * allPlayers.length)]
			: null;

	s.teams.forEach((team) =>
		team.upcoming
			.filter(
				(m) =>
					m.awayRosterId === team.id &&
					moment(m.startDate).isBefore(now.clone().add(14, "days")),
			)
			.forEach((m) =>
				items.push({
					id: "demo-trip-" + m.id,
					department: "teammanager",
					title: `Trasferta a ${m.homeClub}: pullman e pasto da confermare`,
					detail: `${team.name} · ${moment(m.startDate).format("ddd D MMM")}`,
					severity:
						moment(m.startDate).diff(now, "days") <= 3 ? "high" : "medium",
					openedAt: now.clone().subtract(4, "days").toISOString(),
					dueAt: moment(m.startDate).subtract(2, "days").toISOString(),
					preview: true,
				}),
			),
	);

	const player = pick();
	if (!s.teamScope)
		items.push(
			{
				id: "demo-boiler",
				department: "strutture",
				title: "Caldaia spogliatoi settore giovanile guasta",
				detail: "Intervento del tecnico richiesto, in attesa di preventivo",
				severity: "high",
				openedAt: now.clone().subtract(3, "days").toISOString(),
				preview: true,
			},
			{
				id: "demo-pitch",
				department: "strutture",
				title: "Rigenerazione manto campo principale",
				detail: "Da programmare durante la sosta",
				severity: "low",
				openedAt: now.clone().subtract(12, "days").toISOString(),
				dueAt: now.clone().add(20, "days").toISOString(),
				preview: true,
			},
			{
				id: "demo-sponsor",
				department: "commerciale",
				title: "Rinnovo sponsor tecnico: proposta da inviare",
				detail: "Contratto in scadenza a giugno, prima offerta ricevuta",
				severity: "medium",
				openedAt: now.clone().subtract(9, "days").toISOString(),
				dueAt: now.clone().add(35, "days").toISOString(),
				preview: true,
			},
			{
				id: "demo-youth-partner",
				department: "commerciale",
				title: "Partner settore giovanile in scadenza a dicembre",
				detail: "Fissare incontro per il rinnovo",
				severity: "medium",
				openedAt: now.clone().subtract(2, "days").toISOString(),
				dueAt: now.clone().add(40, "days").toISOString(),
				preview: true,
			},
			{
				id: "demo-budget-trips",
				department: "amministrazione",
				title: "Approvare budget trasferte del trimestre",
				detail: "Preventivo pullman e hotel in attesa di firma",
				severity: "medium",
				openedAt: now.clone().subtract(6, "days").toISOString(),
				dueAt: now.clone().add(10, "days").toISOString(),
				preview: true,
			},
			{
				id: "demo-photo-release",
				department: "segreteria",
				title: "Liberatorie foto e video mancanti",
				detail: `${3 + Math.floor(rnd() * 6)} giocatori senza modulo firmato`,
				severity: "low",
				openedAt: now.clone().subtract(15, "days").toISOString(),
				preview: true,
			},
		);
	if (player) {
		items.push({
			id: "demo-clearance-" + player.id,
			department: "segreteria",
			title: `Nulla osta da richiedere: ${fullName(player)}`,
			detail: "Pratica di tesseramento ferma in attesa del club di provenienza",
			severity: "medium",
			openedAt: now.clone().subtract(8, "days").toISOString(),
			dueAt: now.clone().add(7, "days").toISOString(),
			preview: true,
		});
	}
	return items;
};

// Avvenimenti della linea temporale per gli ambiti non ancora gestiti
export const demoTimeline = (s) => {
	const now = moment().startOf("day");
	const events = [];
	const doctor = findStaff(s, /medic/i);
	const coach = findStaff(s, /^allenatore/i);
	const expiring = s.contracts[0];
	const nextSaturday = now
		.clone()
		.day(6)
		.add(now.day() === 6 ? 7 : 0, "days");

	if (expiring) {
		events.push({
			id: "demo-agent",
			date: now.clone().subtract(5, "days").hour(15).toISOString(),
			department: "direzione",
			title: `Incontro con l'agente di ${expiring.playerFirstName} ${expiring.playerLastName}`,
			detail: "Prima discussione sul rinnovo",
			tone: "primary",
			preview: true,
		});
	}
	events.push(
		{
			id: "demo-staff-meeting",
			date: now.clone().day(1).hour(9).toISOString(),
			department: "tecnica",
			title: "Riunione settimanale staff tecnico",
			detail: coach
				? `Con ${fullName(coach)}`
				: "Programmazione della settimana",
			tone: "neutral",
			preview: true,
		},
		{
			id: "demo-doctor",
			date: now.clone().add(2, "days").hour(11).toISOString(),
			department: "medica",
			title: "Punto con lo staff medico",
			detail: doctor
				? `Con ${fullName(doctor)}`
				: "Situazione infortuni e prevenzione",
			tone: "neutral",
			preview: true,
		},
	);
	if (!s.teamScope) {
		events.push(
			{
				id: "demo-boiler",
				date: now.clone().subtract(3, "days").hour(8).toISOString(),
				department: "strutture",
				title: "Segnalato guasto caldaia spogliatoi",
				tone: "warn",
				preview: true,
			},
			{
				id: "demo-sponsor-offer",
				date: now.clone().subtract(9, "days").hour(17).toISOString(),
				department: "commerciale",
				title: "Ricevuta proposta di rinnovo dallo sponsor tecnico",
				tone: "neutral",
				preview: true,
			},
			{
				id: "demo-open-day",
				date: nextSaturday.clone().add(7, "days").hour(10).toISOString(),
				department: "commerciale",
				title: "Open day scuola calcio",
				detail: "Campo principale, 10:00 – 13:00",
				tone: "primary",
				preview: true,
			},
			{
				id: "demo-board",
				date: now.clone().add(9, "days").hour(18).toISOString(),
				department: "amministrazione",
				title: "Consiglio direttivo",
				detail: "Bilancio trimestrale e budget mercato di gennaio",
				tone: "primary",
				preview: true,
			},
			{
				id: "demo-tournament",
				date: now.clone().add(20, "days").hour(12).toISOString(),
				department: "segreteria",
				title: "Scadenza iscrizioni torneo di Natale",
				tone: "warn",
				preview: true,
			},
		);
	}
	return events;
};

// ── Procuratori d'esempio finché il backend non ha mandati e commissioni ─────
// Con lo storico agenti delle schede si usano i procuratori veri; mandati e
// commissioni restano d'esempio, calcolati su ogni contratto reale.
const DEMO_AGENTS = [
	"Agenzia Esempio Sport (esempio)",
	"Studio Delta Management (esempio)",
	"Mario Bianchi, agente (esempio)",
];

export const demoAgency = (s, agentsData) => {
	if (!s) return null;
	const base = s.full || s;
	const now = moment();
	const start = moment(base.seasonStart);
	const contractDetails = agentsData?.contractDetails || new Map();

	let agents;
	if (agentsData?.tracked) {
		agents = agentsData.agents.map((a) => ({...a, preview: false}));
	} else {
		// Assistiti d'esempio: prima chi ha un contratto, poi altri giocatori
		const rnd = seededRandom(base.season?.id || "agenti");
		const withContract = [...base.contractsByPlayer.keys()];
		const others = [...base.playerById.keys()].filter(
			(id) => !base.contractsByPlayer.has(id),
		);
		const picked = [
			...withContract,
			...others.filter(() => rnd() < 0.22).slice(0, 10),
		];
		agents = DEMO_AGENTS.map((name, i) => ({
			id: "demo-agent-" + i,
			name,
			preview: true,
			clients: picked
				.filter((_, k) => k % DEMO_AGENTS.length === i)
				.map((playerId, k) => ({
					playerId,
					since: start
						.clone()
						.subtract(1 + k, "years")
						.format("YYYY-MM-DD"),
				})),
		}));
	}

	const commissions = [];
	agents.forEach((agent, ai) => {
		agent.mandateExpiry = moment()
			.add(4 + ai * 7, "months")
			.endOf("month")
			.format("YYYY-MM-DD");
		agent.clients.forEach((client) => {
			const player = base.playerById.get(client.playerId);
			const teams = base.teamsByPlayer.get(client.playerId) || [];
			(base.contractsByPlayer.get(client.playerId) || []).forEach(
				(contract) => {
					const contractId = contract.contractId || contract.id;
					const detail = contractDetails.get(contractId);
					const rnd = seededRandom(agent.id + contractId);
					const annual =
						detail?.annualGrossSalary ||
						(contract.totalMonthlySalary
							? contract.totalMonthlySalary * 12
							: null);
					const push = (operation, amountBase, rate, dues) =>
						dues.forEach((due, i) => {
							const amount = Math.round((amountBase * rate) / dues.length);
							const late = due.isBefore(now, "day");
							commissions.push({
								id: `${agent.id}-${contractId}-${operation}-${i}`,
								agent,
								playerId: client.playerId,
								player,
								teams,
								operation,
								base: amountBase,
								rate,
								amount,
								dueDate: due.format("YYYY-MM-DD"),
								tranche: dues.length > 1 ? `${i + 1}/${dues.length}` : null,
								status: late ? (rnd() > 0.25 ? "paid" : "overdue") : "due",
								preview: true,
							});
						});
					if (annual) {
						push("Contratto", annual, 0.05, [
							start.clone().add(2, "months"),
							start.clone().add(7, "months"),
						]);
					}
					if (detail?.transferFee > 0) {
						push("Acquisto", detail.transferFee, 0.05, [
							moment(detail.signingDate || detail.startDate || start).add(
								1,
								"months",
							),
						]);
					}
				},
			);
		});
	});
	return {agents, commissions, agentsPreview: !agentsData?.tracked};
};
